import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Eye, Award } from 'lucide-react';
import { MF_DISCLAIMER, COMMISSION_DISCLOSURE } from '@/components/ComplianceNotice';

const About = () => {
  const navigate = useNavigate();

  const values = [
    'Integrity',
    'Transparency',
    'Client-first approach',
    'Financial discipline',
    'Investor education',
    'Long-term relationships',
    'Responsible guidance'
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="about-heading">
              About Impetus Investment Services
            </h1>
            <p className="text-base font-body text-[#4A4A4A] leading-relaxed">
              Bridge to Financial Wellness
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <img
                src="https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Financial consultation"
                className="rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-base font-body text-[#4A4A4A] leading-relaxed">
                <p>
                  Impetus Investment Services is an AMFI-registered Mutual Fund Distributor (ARN: 116347) based in Chetpet, Chennai. We help individuals and families work towards their financial goals through disciplined investing, facilitating mutual fund investments and periodic portfolio reviews.
                </p>
                <p>
                  Our approach begins with understanding the client's goals, time horizon, financial position and risk profile. Based on these factors, we share information on mutual fund schemes that may suit them and facilitate investments and portfolio reviews. Other products such as fixed deposits, insurance, bonds and selected real-estate opportunities are arranged through their respective providers and are not covered by our AMFI registration.
                </p>
                <p>
                  We believe that successful investing is not about reacting to every market movement. It is about maintaining discipline, choosing investments suited to the investor's needs and reviewing the portfolio periodically.
                </p>
                <p>
                  Investor education is an important part of our service. We aim to explain investment concepts in clear language so that clients can make informed financial decisions with confidence.
                </p>
              </div>
            </div>
          </div>

          {/* Mission, Vision, Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5]" data-testid="mission-card">
              <div className="text-[#D97706] mb-4">
                <Target className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-4">Our Mission</h3>
              <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                To help individuals and families pursue financial wellness through informed decisions, disciplined investing and suitable long-term investment solutions.
              </p>
            </div>

            <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5]" data-testid="vision-card">
              <div className="text-[#D97706] mb-4">
                <Eye className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-4">Our Vision</h3>
              <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                To become a trusted financial-services partner known for integrity, personalised guidance, investor education and long-term relationships.
              </p>
            </div>

            <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5]" data-testid="values-card">
              <div className="text-[#D97706] mb-4">
                <Award className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-4">Core Values</h3>
              <ul className="space-y-2">
                {values.map((value, index) => (
                  <li key={index} className="flex items-center text-sm font-body text-[#4A4A4A]">
                    <div className="w-1.5 h-1.5 bg-[#D97706] rounded-full mr-2"></div>
                    {value}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Founders Section */}
          <div className="mb-16" data-testid="founders-section">
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-8 text-center">
              Meet Our Founders
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover text-center" data-testid="founder-card-1">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#A0451E] to-[#D97706] mx-auto mb-4 flex items-center justify-center text-white text-3xl font-heading font-semibold">
                  SA
                </div>
                <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-2">
                  Sravanesh Amara
                </h3>
                <p className="text-sm font-body text-[#D97706] font-semibold mb-3">Co-Founder</p>
                <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                  Leads mutual fund distribution and investor assistance, with a focus on goal-based investing and long-term wealth creation opportunities.
                </p>
              </div>

              <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover text-center" data-testid="founder-card-2">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#A0451E] to-[#D97706] mx-auto mb-4 flex items-center justify-center text-white text-3xl font-heading font-semibold">
                  RN
                </div>
                <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-2">
                  Raghavan N.
                </h3>
                <p className="text-sm font-body text-[#D97706] font-semibold mb-3">Co-Founder</p>
                <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                  Brings deep experience in mutual fund portfolio reviews, risk profiling and personalised investor assistance for every client.
                </p>
              </div>
            </div>
          </div>

          {/* Registration Details */}
          <div className="bg-[#A0451E] text-white rounded-2xl p-12 text-center">
            <h3 className="text-2xl font-heading font-medium mb-4">Registration Details</h3>
            <p className="text-lg font-body mb-1">Impetus Investment Services</p>
            <p className="text-lg font-body mb-2">AMFI-registered Mutual Fund Distributor</p>
            <p className="text-3xl font-heading font-semibold text-[#E8A428] mb-6">ARN: 116347</p>
            <p className="text-sm font-body text-white/80 max-w-2xl mx-auto">
              AMFI registration does not imply approval, recommendation or guarantee of returns. We are not a SEBI-registered Investment Adviser.
            </p>
            <p className="text-sm font-body text-white/80 max-w-2xl mx-auto mt-3" data-testid="about-commission-disclosure">{COMMISSION_DISCLOSURE}</p>
            <p className="text-sm font-body font-semibold text-white mt-3">{MF_DISCLAIMER}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-base font-body text-[#4A4A4A] mb-8 leading-relaxed">
            Let's discuss how we can help you achieve your financial goals through disciplined and goal-based investing.
          </p>
          <button
            onClick={() => navigate('/appointment')}
            className="btn-primary"
            data-testid="about-book-appointment-btn"
          >
            Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;